/**
 * The one place that says what an unsubscribe placeholder looks like.
 *
 * A bulk letter is written in the CRM with a marker where the reader's own
 * opt-out address belongs, and the sender replaces that marker per recipient.
 * Two halves of that contract live in two applications: `web_crm` writes the
 * marker into the body and warns when it is missing, and `@wa/api` replaces it
 * on the way out. If the two ever disagree about the exact characters, the
 * failure is silent and it is the worst kind of silent: the letter still sends,
 * still looks finished, and carries the literal text `{{unsubscribe}}` where the
 * one lawfully required link should be. Nobody reviewing a draft would catch it,
 * because the draft is where the marker is SUPPOSED to be visible.
 *
 * So it is one exported constant in a shared package, not two agreeing string
 * literals. Same reasoning as `phone.ts` in this package: two copies that agree
 * today are a hazard, not a reassurance.
 *
 * Pure module. No clock, no DOM, no imports.
 */

/**
 * The marker an operator puts in the letter body.
 *
 * Double braces because that is what the letter editor already writes and what
 * every operator who has seen a mail template expects. It appears both as link
 * text and inside an `href`, so anything that resolves it has to replace EVERY
 * occurrence rather than the first.
 */
export const UNSUBSCRIBE_PLACEHOLDER = "{{unsubscribe}}";

/** Does this body carry the marker at all. */
export function hasUnsubscribePlaceholder(body: string): boolean {
  return body.includes(UNSUBSCRIBE_PLACEHOLDER);
}

/**
 * Put this recipient's own opt-out address where the marker is.
 *
 * Every occurrence, with `split`/`join` rather than a regular expression: the
 * URL is data and it carries a token, and a token is exactly the kind of string
 * that can contain `$&` or `$1` and be quietly rewritten by a replacement
 * pattern. A UUID cannot today; the function must not depend on that staying
 * true when somebody swaps the token generator.
 */
export function replaceUnsubscribePlaceholder(body: string, url: string): string {
  return body.split(UNSUBSCRIBE_PLACEHOLDER).join(url);
}
